import { trpcVanillaClient } from '@/lib/trpc';

import { faceTecStringsPtBr } from './facetec-strings-pt-br';
import { applyFaceTecTheme } from './facetec-theme';

export type FaceTecFaceScanResultCallback = {
  proceedToNextStep(scanResultBlob: string): void;
  cancel(): void;
  uploadProgress(progress: number): void;
};

export type FaceTecFaceScanProcessor = {
  processSessionResultWhileFaceTecSDKWaits(
    sessionResult: FaceTecSessionResult,
    faceScanResultCallback: FaceTecFaceScanResultCallback
  ): void;
  onFaceTecSDKCompletelyDone(): void;
};

export type FaceTecSessionResult = {
  status: number;
  faceScan: string;
  auditTrail: string[];
  lowQualityAuditTrail: string[];
  sessionId: string;
};

export type FaceTecSDKGlobal = {
  FaceTecSessionStatus: { SessionCompletedSuccessfully: number };
  FaceTecSession?: FaceTecSessionConstructor;
  FaceTecCustomization: new () => Record<string, Record<string, unknown>>;
  FaceTecCancelButtonLocation: { TopRight: unknown };
  initializeInDevelopmentMode(
    deviceKeyIdentifier: string,
    publicFaceScanEncryptionKey: string,
    callback: (initialized: boolean) => void
  ): void;
  initializeInProductionMode(
    productionKeyText: Record<string, string>,
    deviceKeyIdentifier: string,
    publicFaceScanEncryptionKey: string,
    callback: (initialized: boolean) => void
  ): void;
  configureLocalization(strings: Record<string, string>): void;
  setCustomization(customization: Record<string, Record<string, unknown>>): void;
  createFaceTecAPIUserAgentString(sessionId: string): string;
  setResourceDirectory(resourceDirectory: string): void;
  setImagesDirectory(directory: string): void;
  getStatus(): number;
};

export type FaceTecSessionConstructor = new (
  faceScanProcessor: FaceTecFaceScanProcessor,
  sessionToken: string
) => unknown;

export type InitializedSDK = {
  FaceTecSDK: FaceTecSDKGlobal;
  FaceTecSession: FaceTecSessionConstructor;
};

const SDK_SCRIPT_URL = '/core-sdk/FaceTecSDK.js/FaceTecSDK.js';
const SDK_RESOURCE_DIR = '/core-sdk/FaceTecSDK.js/resources';
const SDK_IMAGES_DIR = '/core-sdk/FaceTec_images';

let sdkInitPromise: Promise<InitializedSDK> | null = null;
let sdkInitSessionId: string | undefined = undefined;

export function getInitializedSDK(sessionId?: string): Promise<InitializedSDK> {
  if (sdkInitPromise && sdkInitSessionId === sessionId) {
    return sdkInitPromise;
  }

  sdkInitSessionId = sessionId;
  sdkInitPromise = initializeWithCacheReset(sessionId);

  return sdkInitPromise;
}

async function initializeWithCacheReset(sessionId?: string): Promise<InitializedSDK> {
  try {
    return await doInitializeSDK(sessionId);
  } catch (error) {
    sdkInitPromise = null;
    sdkInitSessionId = undefined;
    throw error;
  }
}

async function doInitializeSDK(sessionId?: string): Promise<InitializedSDK> {
  console.log('[FaceTec] Starting SDK initialization...');

  const keysPromise = sessionId
    ? trpcVanillaClient.facetec.mobileSdkKeys.query({ sessionId })
    : trpcVanillaClient.facetec.sdkKeys.query();

  const [sdkKeys, { FaceTecSDK, FaceTecSession }] = await Promise.all([keysPromise, loadFaceTecScript()]);

  console.log('[FaceTec] SDK script loaded, keys fetched', {
    deviceKeyIdentifier: sdkKeys.deviceKeyIdentifier,
    productionKey: { domains: sdkKeys.productionKey.domains, expiryDate: sdkKeys.productionKey.expiryDate },
    hasPublicEncryptionKey: !!sdkKeys.publicEncryptionKey
  });

  FaceTecSDK.setResourceDirectory(SDK_RESOURCE_DIR);
  FaceTecSDK.setImagesDirectory(SDK_IMAGES_DIR);

  const isLocalDev = window.location.protocol === 'http:';

  await new Promise<void>((resolve, reject) => {
    const onInitComplete = (initialized: boolean) => {
      console.log('[FaceTec] init callback:', initialized, 'status:', FaceTecSDK.getStatus());

      if (initialized) {
        resolve();
      } else {
        reject(new Error(`FaceTec SDK initialization failed (status: ${FaceTecSDK.getStatus()})`));
      }
    };

    if (isLocalDev) {
      console.log('[FaceTec] Using DEVELOPMENT mode (HTTP detected)');
      FaceTecSDK.initializeInDevelopmentMode(sdkKeys.deviceKeyIdentifier, sdkKeys.publicEncryptionKey, onInitComplete);
    } else {
      console.log('[FaceTec] Using PRODUCTION mode (HTTPS detected)', {
        productionKey: { domains: sdkKeys.productionKey.domains, expiryDate: sdkKeys.productionKey.expiryDate }
      });
      FaceTecSDK.initializeInProductionMode(
        sdkKeys.productionKey,
        sdkKeys.deviceKeyIdentifier,
        sdkKeys.publicEncryptionKey,
        onInitComplete
      );
    }
  });

  FaceTecSDK.configureLocalization(faceTecStringsPtBr);
  applyFaceTecTheme(FaceTecSDK);

  console.log('[FaceTec] SDK fully initialized');
  return { FaceTecSDK, FaceTecSession };
}

async function loadFaceTecScript(): Promise<InitializedSDK> {
  const win = window as unknown as Record<string, unknown>;
  const sdk = win.FaceTecSDK as FaceTecSDKGlobal | undefined;

  if (sdk) {
    const session = (win.FaceTecSession as FaceTecSessionConstructor | undefined) ?? sdk.FaceTecSession;

    if (session) {
      return { FaceTecSDK: sdk, FaceTecSession: session };
    }
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_SCRIPT_URL;

    script.onload = () => {
      const loadedSdk = win.FaceTecSDK as FaceTecSDKGlobal | undefined;

      if (!loadedSdk) {
        reject(new Error('FaceTecSDK not found on window after script load'));
        return;
      }

      const session = (win.FaceTecSession as FaceTecSessionConstructor | undefined) ?? loadedSdk.FaceTecSession;

      if (!session) {
        reject(new Error('FaceTecSession not found after script load'));
        return;
      }

      resolve({ FaceTecSDK: loadedSdk, FaceTecSession: session });
    };

    script.onerror = () => reject(new Error('Failed to load FaceTec SDK script'));
    document.head.appendChild(script);
  });
}
